'use client';

import { handleNewsletterSubmit } from 'actions/Website';
import { useActionState, useEffect, useRef, useState } from 'react';
import FormError from './FormError';
import CaretIcon from 'components/icons/CaretIcon';
import UIButton from 'components/buttons/UIButton';
import { captureMailingListSignup } from 'lib/analytics';

type NewsletterFormProps = {
  title: string;
  subtitle: string;
  summary: string;
  buttonText: string;
  tagline: string;
  successTitle: string;
  successMessage: string;
  pageName: string;
  pageUri: string;
  /**
   * Meta's in-app browser scrolls name and email fields into view and offers
   * to autofill them. When set, those fields stay out of the document until
   * the visitor taps the signup button.
   */
  deferContactFields?: boolean;
};

const NewsletterForm = ({
  title,
  subtitle,
  summary,
  buttonText,
  tagline,
  successTitle,
  successMessage,
  pageName,
  pageUri,
  deferContactFields = false,
}: NewsletterFormProps) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [state, formAction, isPending] = useActionState(
    handleNewsletterSubmit,
    {
      data: new FormData(),
      errors: {},
      results: null,
      success: false,
    }
  );

  const [firstName, lastName, email, dob] = [
    'First Name',
    'Last Name',
    'Email',
    'Date of Birth (optional)',
  ];

  const [fieldValues, setFieldValues] = useState({
    firstName: (state.data.get('firstName') as string) || '',
    lastName: (state.data.get('lastName') as string) || '',
    email: (state.data.get('email') as string) || '',
  });

  const handleFieldChange =
    (field: keyof typeof fieldValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const [showContactFields, setShowContactFields] = useState(
    () => !deferContactFields
  );

  // Handle successful submit
  useEffect(() => {
    if (state.success && state.results) {
      captureMailingListSignup(pageName, pageUri);
      queueMicrotask(() => setIsSubmitted(true));
    }
  }, [state.success, state.results, pageName, pageUri]);

  useEffect(() => {
    if (isSubmitted) {
      successHeadingRef.current?.focus();
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (deferContactFields && showContactFields) {
      firstNameRef.current?.focus();
    }
  }, [deferContactFields, showContactFields]);

  if (isSubmitted) {
    return (
      <div className="newsletter-card">
        <div className="submitted" role="status">
          <h3 ref={successHeadingRef} tabIndex={-1}>
            {successTitle}
          </h3>
          {successMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="newsletter-card">
      <div className="newsletter-card-content">
        <h2>{title}</h2>
        <h3>{subtitle}</h3>
        <p>{summary}</p>
      </div>

      {showContactFields ? (
        <form action={formAction} className="newsletter-form-panel">
          <input type="hidden" name="pageName" value={pageName} />
          <input type="hidden" name="pageUri" value={pageUri} />
          <div className="newsletter-form">
            <div className="newsletter-form-fields">
              <div className="newsletter-form-row">
                <label>
                  {firstName}
                  <br />
                  <input
                    ref={firstNameRef}
                    type="text"
                    name="firstName"
                    value={fieldValues.firstName}
                    onChange={handleFieldChange('firstName')}
                    disabled={isPending}
                    required
                    aria-invalid={!!state.errors.firstName}
                    aria-describedby={
                      state.errors.firstName ? 'firstName-error' : undefined
                    }
                  />
                  {state.errors.firstName && (
                    <FormError
                      id="firstName-error"
                      error={state.errors.firstName}
                    />
                  )}
                </label>

                <label>
                  {lastName}
                  <br />
                  <input
                    type="text"
                    name="lastName"
                    value={fieldValues.lastName}
                    onChange={handleFieldChange('lastName')}
                    disabled={isPending}
                    required
                    aria-invalid={!!state.errors.lastName}
                    aria-describedby={
                      state.errors.lastName ? 'lastName-error' : undefined
                    }
                  />
                  {state.errors.lastName && (
                    <FormError
                      id="lastName-error"
                      error={state.errors.lastName}
                    />
                  )}
                </label>
              </div>

              <div className="newsletter-form-row">
                <label>
                  {email}
                  <br />
                  <input
                    type="email"
                    name="email"
                    value={fieldValues.email}
                    onChange={handleFieldChange('email')}
                    disabled={isPending}
                    required
                    aria-invalid={!!state.errors.email}
                    aria-describedby={
                      state.errors.email ? 'email-error' : undefined
                    }
                  />
                  {state.errors.email && (
                    <FormError id="email-error" error={state.errors.email} />
                  )}
                </label>

                <label>
                  {dob}
                  <br />
                  <input
                    type="date"
                    name="dob"
                    defaultValue={state.data.get('dob') as string}
                    disabled={isPending}
                    aria-invalid={!!state.errors.dob}
                    aria-describedby={
                      state.errors.dob ? 'dob-error' : undefined
                    }
                  />
                  {state.errors.dob && (
                    <FormError id="dob-error" error={state.errors.dob} />
                  )}
                </label>
              </div>
            </div>

            <div className="newsletter-form-actions">
              <UIButton
                htmlType="submit"
                buttonType="primary"
                label={buttonText}
                disabled={isPending}
                icon={
                  <CaretIcon size="large" direction="right" color="current" />
                }
              />
              <div className="tagline">{tagline}</div>
            </div>

            {isPending && (
              <div className="spinner" role="status">
                <span className="visually-hidden">Submitting…</span>
              </div>
            )}
          </div>
        </form>
      ) : (
        <div className="newsletter-form-panel">
          <div className="newsletter-form">
            <div className="newsletter-form-actions">
              <UIButton
                htmlType="button"
                buttonType="primary"
                label={buttonText}
                data-testid="newsletter-reveal-form"
                onClick={() => setShowContactFields(true)}
                icon={
                  <CaretIcon size="large" direction="right" color="current" />
                }
              />
              <div className="tagline">{tagline}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterForm;
